import { invalidateProjectFinancials } from "@/lib/invalidateProjectFinancials";
import { useState, useMemo, Fragment } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Pencil, Send, CheckCircle, XCircle, FileText, DollarSign, X, Loader2, UserPlus, Trash2 } from "lucide-react";
import { format, subDays } from "date-fns";
import type { TMTimesheet, TMTimesheetLine, PersonnelProject, TMWorker, RateSchedule } from "@/hooks/usePersonnelProjects";
import { useTMTimesheetLines } from "@/hooks/usePersonnelProjects";
import { AddWorkerDialog } from "./AddWorkerDialog";
import { createNotification } from "@/lib/createNotification";
import { useTimesheetApprovalSetting } from "@/hooks/useTimesheetApprovalSetting";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  draft: { label: "📋 Draft", variant: "secondary" },
  submitted: { label: "📤 Submitted", variant: "outline" },
  client_approved: { label: "✅ Approved", variant: "default" },
  invoiced: { label: "🧾 Invoiced", variant: "default" },
  paid: { label: "💰 Paid", variant: "default" },
};

function weekDescription(weekEnding: string): string {
  const end = new Date(weekEnding);
  const start = subDays(end, 6);
  return `Week of ${format(start, "MMMM d")} – ${format(end, "d, yyyy")}`;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  timesheet: TMTimesheet | null;
  project?: PersonnelProject | null;
  workers?: TMWorker[];
  rates?: RateSchedule[];
}

export function TimesheetDetailDrawer({ open, onOpenChange, timesheet, project, workers, rates }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { can, isAdmin } = usePermissions();
  const { approvalRequired } = useTimesheetApprovalSetting();
  const { data: lines = [] } = useTMTimesheetLines(timesheet?.id);
  const [editing, setEditing] = useState(false);
  const [editLines, setEditLines] = useState<Record<string, Record<string, number>>>({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);

  const canApprove = isAdmin || can("approve_bills");
  const isDraft = timesheet?.status === "draft";
  const sc = timesheet
    ? (statusConfig[timesheet.status] ?? { label: timesheet.status, variant: "secondary" as const })
    : { label: "", variant: "secondary" as const };

  const grouped = useMemo(() => {
    const groups: Record<string, TMTimesheetLine[]> = {};
    lines.forEach((l) => {
      const role = l.tm_rate_schedules?.role_name ?? "Unknown";
      if (!groups[role]) groups[role] = [];
      groups[role].push(l);
    });
    return groups;
  }, [lines]);

  // Workers on this timesheet (to filter add dialog)
  const workersOnTimesheet = useMemo(
    () => new Set(lines.map((l) => (l.tm_workers?.worker_name ?? "").toLowerCase().trim())),
    [lines]
  );

  // Active workers NOT on this timesheet (available to add)
  const availableWorkers = useMemo(
    () => (workers ?? []).filter((w) => w.is_active && !lines.some((l) => l.worker_id === w.id)),
    [workers, lines]
  );

  if (!timesheet) return null;

  const startEditing = () => {
    const map: Record<string, Record<string, number>> = {};
    lines.forEach((l) => {
      map[l.id] = {};
      DAYS.forEach((d) => {
        map[l.id][d] = (l as any)[`${d}_hours`] ?? 0;
      });
    });
    setEditLines(map);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditLines({});
  };

  const updateHour = (lineId: string, day: string, val: number) => {
    setEditLines((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], [day]: val },
    }));
  };

  const getLineTotal = (lineId: string) => {
    const ld = editLines[lineId];
    if (!ld) return 0;
    return DAYS.reduce((s, d) => s + (ld[d] || 0), 0);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      for (const line of lines) {
        const ld = editLines[line.id];
        if (!ld) continue;
        const { error } = await supabase
          .from("tm_timesheet_lines" as any)
          .update({
            mon_hours: ld.mon ?? 0,
            tue_hours: ld.tue ?? 0,
            wed_hours: ld.wed ?? 0,
            thu_hours: ld.thu ?? 0,
            fri_hours: ld.fri ?? 0,
            sat_hours: ld.sat ?? 0,
            sun_hours: ld.sun ?? 0,
          })
          .eq("id", line.id);
        if (error) throw error;
      }
      qc.invalidateQueries({ queryKey: ["tm_timesheet_lines", timesheet.id] });
      qc.invalidateQueries({ queryKey: ["tm_timesheets", timesheet.personnel_project_id] });
      toast.success("Hours updated");
      setEditing(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Add worker to timesheet ──────────────────────────────────────────
  const addWorkerToTimesheet = async (worker: TMWorker) => {
    if (!timesheet) return;
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      const { data: newLine, error } = await supabase
        .from("tm_timesheet_lines" as any)
        .insert({
          company_id: companyId,
          timesheet_id: timesheet.id,
          personnel_project_id: timesheet.personnel_project_id,
          worker_id: worker.id,
          rate_schedule_id: worker.rate_schedule_id,
          mon_hours: 0, tue_hours: 0, wed_hours: 0, thu_hours: 0,
          fri_hours: 0, sat_hours: 0, sun_hours: 0,
        })
        .select()
        .single();
      if (error) throw error;
      // If currently editing, add the new line to editLines so it's immediately editable
      if (editing && newLine) {
        setEditLines((prev) => ({
          ...prev,
          [(newLine as any).id]: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
        }));
      }
      qc.invalidateQueries({ queryKey: ["tm_timesheet_lines", timesheet.id] });
      qc.invalidateQueries({ queryKey: ["tm_timesheets", timesheet.personnel_project_id] });
      toast.success(`${worker.worker_name} added to timesheet`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // ── Remove worker from timesheet ───────────────────────────────────
  const removeWorkerFromTimesheet = async (lineId: string, workerName: string) => {
    if (!timesheet) return;
    try {
      const { error } = await supabase
        .from("tm_timesheet_lines" as any)
        .delete()
        .eq("id", lineId);
      if (error) throw error;
      // Also remove from editLines state if editing
      if (editing) {
        setEditLines((prev) => {
          const next = { ...prev };
          delete next[lineId];
          return next;
        });
      }
      qc.invalidateQueries({ queryKey: ["tm_timesheet_lines", timesheet.id] });
      qc.invalidateQueries({ queryKey: ["tm_timesheets", timesheet.personnel_project_id] });
      toast.success(`${workerName} removed from timesheet`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };


  const transitionStatus = async (newStatus: string) => {
    setSaving(true);
    try {
      const updates: Record<string, any> = { status: newStatus };
      if (newStatus === "submitted") updates.submitted_at = new Date().toISOString();
      if (newStatus === "client_approved") {
        updates.approved_at = new Date().toISOString();
        updates.approved_by_name = user?.email ?? "Unknown";
        // When skipping approval, also set submitted_at
        if (!approvalRequired) updates.submitted_at = new Date().toISOString();
      }
      // Reject: return to draft, clear submitted_at
      if (newStatus === "draft") {
        updates.submitted_at = null;
        updates.approved_at = null;
        updates.approved_by_name = null;
      }

      const { error } = await supabase
        .from("tm_timesheets" as any)
        .update(updates)
        .eq("id", timesheet.id);
      if (error) throw error;

      qc.invalidateQueries({ queryKey: ["tm_timesheets", timesheet.personnel_project_id] });
      const label = newStatus === "draft" ? "rejected (returned to draft)" : newStatus.replace("_", " ");
      toast.success(`Timesheet ${label}`);

      // Create notification for the status change
      const weekLabel = `Week ending ${format(new Date(timesheet.week_ending), "MMM d, yyyy")}`;
      const projectName = project?.name ?? "Personnel Project";
      if (newStatus === "submitted") {
        createNotification({
          entity_type: "timesheet", entity_id: timesheet.id,
          entity_label: weekLabel, project_id: (project as any)?.project_id ?? null,
          type: "timesheet_submitted", title: "Timesheet Submitted",
          message: `${projectName} — ${weekLabel} submitted for approval.`,
          priority: "normal", action_label: "Review", action_url: `/controller/personnel/${timesheet.personnel_project_id}?tab=timesheets`,
          created_by: user?.id,
        });
      } else if (newStatus === "client_approved") {
        createNotification({
          entity_type: "timesheet", entity_id: timesheet.id,
          entity_label: weekLabel, project_id: (project as any)?.project_id ?? null,
          type: "timesheet_approved", title: "Timesheet Approved",
          message: `${projectName} — ${weekLabel} has been approved.`,
          priority: "normal", action_label: "View",
          action_url: `/controller/personnel/${timesheet.personnel_project_id}?tab=timesheets`,
          created_by: user?.id,
        });
      } else if (newStatus === "draft") {
        createNotification({
          entity_type: "timesheet", entity_id: timesheet.id,
          entity_label: weekLabel, project_id: (project as any)?.project_id ?? null,
          type: "timesheet_rejected", title: "Timesheet Rejected",
          message: `${projectName} — ${weekLabel} was rejected and returned to draft.`,
          priority: "high", action_label: "Revise",
          action_url: `/controller/personnel/${timesheet.personnel_project_id}?tab=timesheets`,
          created_by: user?.id,
        });
      } else if (newStatus === "paid") {
        createNotification({
          entity_type: "timesheet", entity_id: timesheet.id,
          entity_label: weekLabel, project_id: (project as any)?.project_id ?? null,
          type: "timesheet_paid", title: "Timesheet Marked Paid",
          message: `${projectName} — ${weekLabel} marked as paid.`,
          priority: "low", created_by: user?.id,
        });
      }

      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Generate Customer Invoice + Vendor Bills ─────────────────────────
  const generateInvoiceAndBills = async () => {
    if (!project) return toast.error("Project data not available");
    setGenerating(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const weekDesc = weekDescription(timesheet.week_ending);
      const weekNum = timesheet.week_number ?? 1;
      const invoiceNumber = `${project.project_number ?? "PP"}-W${String(weekNum).padStart(2, "0")}`;

      // ── 1. Customer Invoice (bill rate, grouped by role) ──────────
      // Group lines by role
      // Build invoice lines directly from timesheet lines (respects per-worker overrides via trigger-computed totals)
      const invoiceLines: { description: string; quantity: number; unit_price: number; line_total: number }[] = [];
      const workerMapForInv = new Map<string, TMWorker>();
      for (const w of workers ?? []) workerMapForInv.set(w.id, w);

      for (const line of lines) {
        const worker = workerMapForInv.get(line.worker_id);
        const workerName = worker?.worker_name ?? "Unknown";
        const billRate = worker?.bill_rate_override ?? line.tm_rate_schedules?.bill_rate_regular ?? 0;
        const billRateOt = worker?.bill_rate_override ?? line.tm_rate_schedules?.bill_rate_ot ?? 0;

        if ((line.regular_hours ?? 0) > 0) {
          invoiceLines.push({
            description: `${workerName} — Regular Time`,
            quantity: line.regular_hours ?? 0,
            unit_price: billRate,
            line_total: line.regular_billed ?? 0,
          });
        }
        if ((line.ot_hours ?? 0) > 0) {
          invoiceLines.push({
            description: `${workerName} — Overtime`,
            quantity: line.ot_hours ?? 0,
            unit_price: billRateOt,
            line_total: line.ot_billed ?? 0,
          });
        }
      }

      const invoiceSubtotal = invoiceLines.reduce((s, l) => s + l.line_total, 0);

      // Insert invoice
      const { data: invoice, error: invErr } = await supabase
        .from("invoices")
        .insert({
          company_id: companyId,
          project_id: (project as any).project_id ?? null,
          customer_id: project.customer_id ?? null,
          invoice_number: invoiceNumber,
          invoice_date: timesheet.week_ending,
          invoice_type: "standard",
          subtotal: invoiceSubtotal,
          total: invoiceSubtotal,
          status: "draft",
          notes: weekDesc,
          created_by: user?.id,
        } as any)
        .select("id")
        .single();
      if (invErr) throw invErr;

      // Insert invoice lines
      if (invoiceLines.length > 0) {
        const { error: linesErr } = await supabase
          .from("invoice_lines")
          .insert(invoiceLines.map((l) => ({
            company_id: companyId,
            invoice_id: invoice.id,
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
            line_total: l.line_total,
          })) as any);
        if (linesErr) throw linesErr;
      }

      // ── 2. Vendor Bills (cost rate, per worker) ───────────────────
      const workerMap = new Map<string, TMWorker>();
      for (const w of workers ?? []) workerMap.set(w.id, w);

      for (const line of lines) {
        if ((line.total_hours ?? 0) <= 0) continue;

        const worker = workerMap.get(line.worker_id);
        if (!worker) continue;

        let vendorId = worker.vendor_id;
        // Create vendor if needed
        if (!vendorId) {
          const nameParts = worker.worker_name.split(" ");
          const firstName = nameParts[0] || worker.worker_name;
          const lastName = nameParts.slice(1).join(" ") || "";
          try {
            const { data: newVendorId } = await supabase.rpc("create_vendor_for_worker", {
              p_company_id: companyId,
              p_first_name: firstName,
              p_last_name: lastName,
              p_created_by: user?.id,
            });
            vendorId = newVendorId;
            // Update worker with vendor_id
            await supabase.from("tm_workers" as any).update({ vendor_id: vendorId }).eq("id", worker.id);
          } catch {
            // Non-blocking: create bill without vendor link
          }
        }

        const costRate = worker.cost_rate_override ?? line.tm_rate_schedules?.cost_rate ?? 0;
        const regularCost = (line.regular_hours ?? 0) * costRate;
        const otCost = (line.ot_hours ?? 0) * costRate;
        const billTotal = regularCost + otCost;

        const initials = worker.worker_name.split(" ").map((n) => n[0]?.toUpperCase() ?? "").join("");
        const billNumber = `${project.project_number ?? "PP"}-${initials}-W${String(weekNum).padStart(2, "0")}`;

        const billLines: { description: string; quantity: number; unit_price: number; line_total: number }[] = [];
        if ((line.regular_hours ?? 0) > 0) {
          billLines.push({
            description: `${worker.worker_name} — Regular Time`,
            quantity: line.regular_hours ?? 0,
            unit_price: costRate,
            line_total: regularCost,
          });
        }
        if ((line.ot_hours ?? 0) > 0) {
          billLines.push({
            description: `${worker.worker_name} — Overtime`,
            quantity: line.ot_hours ?? 0,
            unit_price: costRate,
            line_total: otCost,
          });
        }

        if (billLines.length === 0) continue;

        const { data: bill, error: billErr } = await supabase
          .from("bills")
          .insert({
            company_id: companyId,
            project_id: (project as any).project_id ?? null,
            vendor_id: vendorId ?? null,
            bill_number: billNumber,
            bill_date: timesheet.week_ending,
            description: weekDesc,
            subtotal: billTotal,
            retainage_amount: 0,
            retainage_percent: 0,
            total: billTotal,
            status: "draft",
          } as any)
          .select("id")
          .single();
        if (billErr) throw billErr;

        const { error: blErr } = await supabase
          .from("bill_lines")
          .insert(billLines.map((l) => ({
            company_id: companyId,
            bill_id: bill.id,
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
            line_total: l.line_total,
          })) as any);
        if (blErr) throw blErr;
      }

      // ── 3. Update timesheet status ────────────────────────────────
      await supabase
        .from("tm_timesheets" as any)
        .update({ status: "invoiced", invoice_id: invoice.id })
        .eq("id", timesheet.id);

      qc.invalidateQueries({ queryKey: ["tm_timesheets", timesheet.personnel_project_id] });
      qc.invalidateQueries({ queryKey: ["personnel_invoices"] });
      qc.invalidateQueries({ queryKey: ["personnel_vendor_bills"] });
      invalidateProjectFinancials(qc, (project as any).project_id ?? null);
      toast.success("Customer invoice and vendor bills generated");

      createNotification({
        entity_type: "timesheet", entity_id: timesheet.id,
        entity_label: weekDesc, project_id: (project as any).project_id ?? null,
        type: "invoice_generated", title: "Invoice & Bills Generated",
        message: `${project.name} — ${weekDesc}: customer invoice and ${lines.length} vendor bill(s) created.`,
        priority: "normal", action_label: "View Invoices",
        action_url: `/controller/personnel/${project.id}?tab=invoices`,
        created_by: user?.id,
      });

      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Week Ending {format(new Date(timesheet.week_ending), "MMM d, yyyy")}
            <Badge variant={sc.variant}>{sc.label}</Badge>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Review timesheet hours, cost, margin, and approval actions for this pay period.
          </SheetDescription>
        </SheetHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Hours</p>
              <p className="text-lg font-bold">{timesheet.total_regular_hours + timesheet.total_ot_hours}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Regular Hours</p>
              <p className="text-lg font-bold">{timesheet.total_regular_hours}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">OT Hours</p>
              <p className="text-lg font-bold">{timesheet.total_ot_hours}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Cost</p>
              <p className="text-lg font-bold">{fmt(timesheet.total_cost)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Margin</p>
              <p className={`text-lg font-bold ${timesheet.margin_pct >= 25 ? "text-emerald-600" : timesheet.margin_pct >= 15 ? "text-amber-600" : "text-destructive"}`}>
                {timesheet.margin_pct.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4">
          {isDraft && !editing && (
            <>
              <Button size="sm" variant="outline" onClick={startEditing}>
                <Pencil className="mr-1 h-3 w-3" /> Edit Hours
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAddWorkerOpen(true)}>
                <UserPlus className="mr-1 h-3 w-3" /> Add Worker
              </Button>
              <Button size="sm" onClick={() => transitionStatus(approvalRequired ? "submitted" : "client_approved")} disabled={saving}>
                <Send className="mr-1 h-3 w-3" /> {approvalRequired ? "Submit" : "Submit & Approve"}
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button size="sm" onClick={saveEdits} disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAddWorkerOpen(true)}>
                <UserPlus className="mr-1 h-3 w-3" /> Add Worker
              </Button>
              <Button size="sm" variant="outline" onClick={cancelEditing}>
                <X className="mr-1 h-3 w-3" /> Cancel
              </Button>
            </>
          )}
          {timesheet.status === "submitted" && canApprove && approvalRequired && (
            <>
              <Button size="sm" onClick={() => transitionStatus("client_approved")} disabled={saving}>
                <CheckCircle className="mr-1 h-3 w-3" /> Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => transitionStatus("draft")} disabled={saving}>
                <XCircle className="mr-1 h-3 w-3" /> Reject
              </Button>
            </>
          )}
          {timesheet.status === "submitted" && !canApprove && approvalRequired && (
            <p className="text-xs text-muted-foreground italic">Awaiting approval from accounting…</p>
          )}
          {timesheet.status === "client_approved" && (
            <Button size="sm" onClick={generateInvoiceAndBills} disabled={generating}>
              {generating ? (
                <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Generating…</>
              ) : (
                <><FileText className="mr-1 h-3 w-3" /> Generate Invoice &amp; Bills</>
              )}
            </Button>
          )}
          {timesheet.status === "invoiced" && (
            <Button size="sm" onClick={() => transitionStatus("paid")} disabled={saving}>
              <DollarSign className="mr-1 h-3 w-3" /> Mark Paid
            </Button>
          )}
        </div>

        {/* Lines Table */}
        <div className="overflow-x-auto mt-4">
          {lines.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No timesheet lines</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[120px]">Worker</TableHead>
                  {DAY_LABELS.map((d, i) => (
                    <TableHead key={i} className="w-14 text-center">{d}</TableHead>
                  ))}
                  <TableHead className="text-right w-14">Hrs</TableHead>
                  <TableHead className="text-right w-20">Cost</TableHead>
                  {(editing || isDraft) && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(grouped).map(([role, roleLines]) => (
                  <Fragment key={role}>
                    <TableRow className="bg-muted/30">
                      <TableCell colSpan={10} className="font-semibold text-xs uppercase tracking-wider py-1">
                        {role}
                      </TableCell>
                    </TableRow>
                    {roleLines.map((l) => {
                      const total = editing
                        ? getLineTotal(l.id)
                        : (l.total_hours ?? 0);
                      return (
                        <TableRow key={l.id}>
                          <TableCell className="text-sm">{l.tm_workers?.worker_name ?? "—"}</TableCell>
                          {DAYS.map((d) => (
                            <TableCell key={d} className="p-1 text-center">
                              {editing ? (
                                <Input
                                  type="number"
                                  className="h-7 w-12 text-center text-sm"
                                  value={editLines[l.id]?.[d] || ""}
                                  onChange={(e) => updateHour(l.id, d, parseFloat(e.target.value) || 0)}
                                  min={0}
                                  max={24}
                                />
                              ) : (
                                <span className="text-sm">{(l as any)[`${d}_hours`] ?? 0}</span>
                              )}
                            </TableCell>
                          ))}
                          <TableCell className="text-right font-medium">{total}</TableCell>
                          <TableCell className="text-right text-sm">{fmt(l.total_cost ?? 0)}</TableCell>
                          {(editing || isDraft) && (
                            <TableCell className="p-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                title="Remove from timesheet"
                                onClick={() => removeWorkerFromTimesheet(l.id, l.tm_workers?.worker_name ?? "Worker")}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Quick-add available workers when editing */}
        {(isDraft || editing) && availableWorkers.length > 0 && (
          <div className="mt-4 border rounded-md p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Add from Workers Tab</p>
            <div className="flex flex-wrap gap-2">
              {availableWorkers.slice(0, 8).map((w) => {
                const rate = w.tm_rate_schedules;
                return (
                  <Button
                    key={w.id}
                    variant="outline"
                    size="sm"
                    className="h-auto py-1 px-2 text-xs"
                    onClick={() => addWorkerToTimesheet(w)}
                  >
                    <UserPlus className="mr-1 h-3 w-3" />
                    {w.worker_name}
                    {rate && <span className="text-muted-foreground ml-1">({rate.role_name})</span>}
                  </Button>
                );
              })}
              {availableWorkers.length > 8 && (
                <Button variant="outline" size="sm" className="h-auto py-1 px-2 text-xs" onClick={() => setAddWorkerOpen(true)}>
                  +{availableWorkers.length - 8} more…
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Add Worker Dialog (for adding new workers to project + timesheet) */}
        {addWorkerOpen && project && rates && (
          <AddWorkerDialog
            open={addWorkerOpen}
            onOpenChange={setAddWorkerOpen}
            personnelProjectId={project.id}
            rates={rates}
            existingWorkerNames={workersOnTimesheet}
            onWorkerAdded={(newWorker) => addWorkerToTimesheet(newWorker)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
